import type { MenuType } from '../components/Navigation/src/data/data.types'
import { keyFor } from './cache-version'

const PREFIX = 'nav_footer_cache'

/**
 * Delete sessionStorage items whose keys match the given url.
 */
function deleteSessionStorage(url: string): void {
  const urlSplit = url.split('/')
  const currentUrl = urlSplit[urlSplit.length - 1]
  const toRemove = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key && key.includes(PREFIX) && key.includes(currentUrl)) {
      toRemove.push(key)
    }
  }
  toRemove.forEach(k => sessionStorage.removeItem(k))
}

/**
 * GET-only cache with TTL (default: 30 days).
 * On miss/expire → network → cache result.
 */
export async function cacheGet(
  url: string,
  { ttlMs = 30 * 24 * 60 * 60 * 1000 } = {}
): Promise<MenuType> {
  const key = keyFor(url)
  // Try serve from sessionStorage
  try {
    const raw = sessionStorage.getItem(key)
    if (raw) {
      const { expiry, data } = JSON.parse(raw)
      if (expiry > Date.now()) {
        return {
          status: 'fetched',
          error: null,
          data,
        }
      }
      sessionStorage.removeItem(key) // expired
    }
  } catch {
    return {
      status: 'fetched',
      error: 'Error while loading' + url?.includes('footer') ? 'footer' : 'navigation',
      data: { name: '', menuItems: [] },
    }
  }

  if (url) {
    // Network fetch
    const res = await fetch(url, { method: 'GET', credentials: 'include' })
    if (!res.ok) throw new Error(`GET ${url} failed: ${res.status} ${res.statusText}`)

    const data = await res.json()
    try {
      deleteSessionStorage(url)
      sessionStorage.setItem(
        key,
        JSON.stringify({
          expiry: Date.now() + ttlMs,
          data,
        })
      )
    } catch {
      return {
        status: 'fetched',
        error: 'Error while loading' + url?.includes('footer') ? 'footer' : 'navigation',
        data: { name: '', menuItems: [] },
      }
    }

    return {
      status: 'fetched',
      error: null,
      data,
    }
  } else {
    return {
      status: 'fetched',
      error: null,
      data: { name: '', menuItems: [] },
    }
  }
}
